import { apiClient } from '../lib/apiClient';

export type JournalEntryVisibility = 'public' | 'private';

/**
 * A gallery image attached to a journal entry. Mirrors `AttachedImage` in
 * src/types/journalEntry.ts (the `attachedImages` field of
 * GET /aquariums/:aquariumId/journal-entries/:entryId).
 */
export interface AttachedImage {
  id: string;
  aquariumId: string;
  s3Key: string;
  displayUrl: string | null;
  thumbnailUrl: string | null;
  originalUrl: string | null;
  createdAt: string;
}

/**
 * The first photo linked to an entry, included on every list item of
 * GET /aquariums/:aquariumId/journal-entries (`coverImage`, null when the entry
 * has no photo). Mirrors `JournalCoverImage` in src/types/journalEntry.ts.
 */
export interface JournalCoverImage {
  id: string;
  thumbnailUrl: string | null;
  displayUrl: string | null;
  /** The image's note, used as alt text when present. */
  note: string | null;
}

/**
 * A water-parameter measurement attached to a journal entry. Mirrors
 * `AttachedMeasurement` in src/types/journalEntry.ts (same shape as every
 * other measurement response, see mapMeasurementRow).
 */
export interface AttachedMeasurement {
  id: string;
  aquariumId: string;
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
  createdAt: string;
  /** 'YYYY-MM-DD' when the value has no time of day (date-only ICP analysis), else null. */
  measuredDate?: string | null;
}

/** Body of POST .../measurements/record — the same as POST /aquariums/:aquariumId/measurements. */
export interface RecordMeasurementPayload {
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
}

export interface JournalEntry {
  id: string;
  aquariumId: string;
  authorId: string;
  title: string;
  content: string;
  visibility: JournalEntryVisibility;
  tags: string[];
  /** First linked photo — only present on list items (null when there is none). */
  coverImage?: JournalCoverImage | null;
  /** Only present on the single-entry GET response, never on list items. */
  attachedImages?: AttachedImage[];
  /** Only present on the single-entry GET response, never on list items. */
  attachedMeasurements?: AttachedMeasurement[];
  createdAt: string;
  updatedAt: string;
  /** Calendar day of the entry (YYYY-MM-DD); relative due dates of detected tasks count from it. */
  entryDate?: string | null;
  /** 'manual' or 'voice' (saved from a voice note, docs/voiceFeature.md). */
  source?: string;
  /** The voice note an entry was saved from. */
  voiceCaptureId?: string | null;
  /** Tasks created from this entry (single-entry GET only). */
  linkedTasks?: JournalLinkedTask[];
  /**
   * Create/update responses only: the text has follow-up signal words ("in 3 days",
   * "check again") — offer the (paid) task detection. From `meta.taskDetectionSuggested`.
   */
  taskDetectionSuggested?: boolean;
}

/** A task created from a journal entry (`linkedTasks` of the entry GET). */
export interface JournalLinkedTask {
  id: string;
  title: string;
  dueDate: string | null;
  status: string;
  isRecurring: boolean;
}

export interface CreateJournalEntryPayload {
  title: string;
  content: string;
  visibility?: JournalEntryVisibility;
  tags?: string[];
}

export interface UpdateJournalEntryPayload {
  title?: string;
  content?: string;
  visibility?: JournalEntryVisibility;
  tags?: string[];
}

export interface JournalEntryListParams {
  page?: number;
  limit?: number;
  search?: string;
  tag?: string;
  visibility?: JournalEntryVisibility;
}

export interface JournalEntryListResponse {
  entries: JournalEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function buildQueryString(params: JournalEntryListParams): string {
  const parts: string[] = [];
  if (params.page !== undefined) parts.push(`page=${encodeURIComponent(String(params.page))}`);
  if (params.limit !== undefined) parts.push(`limit=${encodeURIComponent(String(params.limit))}`);
  if (params.search !== undefined && params.search !== '') {
    parts.push(`search=${encodeURIComponent(params.search)}`);
  }
  if (params.tag !== undefined && params.tag !== '') {
    parts.push(`tag=${encodeURIComponent(params.tag)}`);
  }
  if (params.visibility !== undefined) {
    parts.push(`visibility=${encodeURIComponent(params.visibility)}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * One page of the user's entries of an aquarium. Search (title + content),
 * tag and visibility are filtered server-side; `total` / `totalPages` count the
 * filtered set. Backend: GET .../journal-entries → { data, meta }.
 */
export async function listJournalEntries(
  aquariumId: string,
  params: JournalEntryListParams = {},
): Promise<JournalEntryListResponse> {
  const qs = buildQueryString(params);
  const response = await apiClient.get<{
    data: JournalEntry[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }>(`/api/v1/aquariums/${aquariumId}/journal-entries${qs}`);
  return {
    entries: response.data,
    total: response.meta.total,
    page: response.meta.page,
    limit: response.meta.limit,
    totalPages: response.meta.totalPages,
  };
}

/** The saved entry plus the server's hint that its text may contain follow-up tasks (UC-B). */
function withDetectionHint(response: { data: JournalEntry; meta?: { taskDetectionSuggested?: boolean } }): JournalEntry {
  return response.meta?.taskDetectionSuggested === true ? { ...response.data, taskDetectionSuggested: true } : response.data;
}

export async function getJournalEntry(
  aquariumId: string,
  entryId: string,
): Promise<JournalEntry> {
  const response = await apiClient.get<{ data: JournalEntry }>(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}`,
  );
  return response.data;
}

export async function createJournalEntry(
  aquariumId: string,
  payload: CreateJournalEntryPayload,
): Promise<JournalEntry> {
  const response = await apiClient.post<{ data: JournalEntry; meta?: { taskDetectionSuggested?: boolean } }>(
    `/api/v1/aquariums/${aquariumId}/journal-entries`,
    payload,
  );
  return withDetectionHint(response);
}

export async function updateJournalEntry(
  aquariumId: string,
  entryId: string,
  payload: UpdateJournalEntryPayload,
): Promise<JournalEntry> {
  const response = await apiClient.put<{ data: JournalEntry; meta?: { taskDetectionSuggested?: boolean } }>(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}`,
    payload,
  );
  return withDetectionHint(response);
}

export async function deleteJournalEntry(
  aquariumId: string,
  entryId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}`,
  );
}

/**
 * Links an already-uploaded gallery image to a journal entry.
 *
 * This is step two of the two-step upload-then-link pattern: the image must
 * already exist (via the aquarium image upload endpoint) before it can be
 * linked here. Backend route: src/routes/journalEntryPhotos.ts, mounted at
 * /api/v1/aquariums/:aquariumId/journal-entries/:entryId/images in app.ts.
 */
export async function linkPhotoToEntry(
  aquariumId: string,
  entryId: string,
  imageId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}/images`,
    { imageId },
  );
}

/**
 * Unlinks a photo from a journal entry.
 *
 * This removes only the join-table row — the underlying gallery image is
 * never deleted and remains browsable elsewhere (e.g. the gallery page).
 */
export async function unlinkPhotoFromEntry(
  aquariumId: string,
  entryId: string,
  imageId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}/images/${imageId}`,
  );
}

/**
 * Links an existing measurement of the aquarium to a journal entry.
 * Backend: POST .../journal-entries/:entryId/measurements { measurementIds } (1..50).
 */
export async function linkMeasurement(
  aquariumId: string,
  entryId: string,
  measurementId: string,
): Promise<void> {
  await apiClient.post(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}/measurements`,
    { measurementIds: [measurementId] },
  );
}

/** Removes only the entry–measurement link; the measurement itself is kept. */
export async function unlinkMeasurement(
  aquariumId: string,
  entryId: string,
  measurementId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}/measurements/${measurementId}`,
  );
}

/**
 * Creates a measurement and links it to the entry in ONE server-side
 * transaction (POST .../measurements/record). Never replace this with a plain
 * create followed by a separate link call.
 */
export async function recordAndLinkMeasurement(
  aquariumId: string,
  entryId: string,
  payload: RecordMeasurementPayload,
): Promise<AttachedMeasurement> {
  const response = await apiClient.post<{ data: { measurement: AttachedMeasurement } }>(
    `/api/v1/aquariums/${aquariumId}/journal-entries/${entryId}/measurements/record`,
    payload,
  );
  return response.data.measurement;
}
